"use client";

// src/context/CartContext.tsx
import { createContext, useContext, useState, useEffect, useMemo, useCallback, ReactNode } from 'react';
import Image from 'next/image';
import { useFreeProduct } from '../hooks/useFreeProduct';
import { applyFreeProductPrice, isProductAvailable, isFreeProduct } from '../utils/productUtils';
import { Product } from '../types';
import toast from 'react-hot-toast';
import { IoClose } from 'react-icons/io5';
import { triggerConfetti } from '../utils/confetti';
import { showBulkOrderNotification, showFreeProductLimitNotification } from '../utils/bulkOrderNotification';
import {
  getCartAnalytics,
  trackAddToCartCompleted,
} from '../lib/posthog';

export type CartItem = Product & { quantity: number };

type CartContextType = {
  cartItems: CartItem[];
  addToCart: (product: Product) => void;
  removeFromCart: (productId: string) => void;
  updateQuantity: (productId: string, newQuantity: number) => void;
  replaceCart: (items: CartItem[]) => void;
  clearCart: () => void;
  getItemQuantity: (productId: string) => number;
  addFreeGiftToCart: (productOverride?: Product) => void;
  itemCount: number;
  isAnimating: boolean;
  isCartDrawerOpen: boolean;
  openCartDrawer: () => void;
  closeCartDrawer: () => void;
  toggleCartDrawer: () => void;
};

const CartContext = createContext<CartContextType | undefined>(undefined);

// Simple notification sound
const playNotificationSound = () => {
  try {
    const realAudio = new Audio("https://assets.mixkit.co/active_storage/sfx/2358/2358-preview.mp3");
    realAudio.volume = 0.5;
    realAudio.play().catch(e => console.log("Audio play failed", e));
  } catch (e) {
    console.error("Error playing sound", e);
  }
};

// triggerConfetti is now a shared async util (src/utils/confetti.ts)

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isCartDrawerOpen, setIsCartDrawerOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const { freeProduct } = useFreeProduct(
    isInitialized && (cartItems.length > 0 || isCartDrawerOpen),
  );

  const openCartDrawer = useCallback(() => setIsCartDrawerOpen(true), []);
  const closeCartDrawer = useCallback(() => setIsCartDrawerOpen(false), []);
  const toggleCartDrawer = useCallback(() => setIsCartDrawerOpen(prev => !prev), []);

  useEffect(() => {
    try {
      const storedCart = localStorage.getItem('astroji-cart');
      if (storedCart) {
        const parsedCart: CartItem[] = JSON.parse(storedCart);
        // Migrations: ensure free items have price 0
        const migrationCart = parsedCart.map(item => applyFreeProductPrice(item) as CartItem);
        setCartItems(migrationCart);
      } else {
        setCartItems([]);
      }
    } catch (error) {
      console.error("Failed to parse cart from localStorage", error);
      setCartItems([]);
    } finally {
      setIsInitialized(true);
    }
  }, []);

  useEffect(() => {
    if (!isInitialized) return;

    if (cartItems.length > 0) {
      localStorage.setItem('astroji-cart', JSON.stringify(cartItems));
    } else {
      localStorage.removeItem('astroji-cart');
    }
  }, [cartItems, isInitialized]);

  const triggerAnimation = useCallback(() => {
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 500);
  }, []);

  const addToCart = useCallback((productRaw: Product) => {
    // Apply free product price override (set price to 0 if product has 'free' tag)
    const product = applyFreeProductPrice(productRaw);

    // Safety check: Block unavailable products (invalid shiprocketId)
    if (!isProductAvailable(product)) {
      toast.error('This product is currently out of stock.', {
        duration: 3000,
        position: 'top-center',
      });
      return;
    }

    setCartItems((prevCartItems) => {
      const existingItem = prevCartItems.find((item) => item.id === product.id);
      let nextCartItems: CartItem[];

      if (existingItem) {
        if (isFreeProduct(existingItem)) {
          showFreeProductLimitNotification();
          return prevCartItems;
        }

        if (existingItem.quantity >= 5) {
          showBulkOrderNotification();
          return prevCartItems;
        }
        nextCartItems = prevCartItems.map((item) =>
          item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      } else {
        if (isFreeProduct(product)) {
          const hasFreeProduct = prevCartItems.some(item => isFreeProduct(item));
          if (hasFreeProduct) {
            showFreeProductLimitNotification();
            return prevCartItems;
          }
        }
        nextCartItems = [...prevCartItems, { ...product, quantity: 1 }];
      }

      // Defer analytics tracking to idle / async tick so main thread painting is never delayed
      const cartBeforeCount = prevCartItems.reduce((total, item) => total + item.quantity, 0);
      const deferTracking = () => {
        try {
          trackAddToCartCompleted(product, {
            quantity_added: 1,
            cart_status_before: cartBeforeCount,
            confetti_triggered: false,
            ...getCartAnalytics(nextCartItems),
          });

          if (window.fbq) {
            window.fbq('track', 'AddToCart', {
              content_name: product.name,
              content_ids: [product.id],
              content_type: 'product',
              value: product.price,
              currency: 'INR'
            });
          }
        } catch (e) {
          console.error('Error tracking AddToCart:', e);
        }
      };

      if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        window.requestIdleCallback(deferTracking);
      } else {
        setTimeout(deferTracking, 0);
      }

      return nextCartItems;
    });

    triggerAnimation();

    // Yield sound and toast notification to next animation frame/tick for ultra-responsive INP
    setTimeout(() => {
      playNotificationSound();
      toast.dismiss();
      toast.custom((t) => (
        <div
          className={`${t.visible ? 'animate-drop-in' : 'animate-drop-out'
            } max-w-md w-full bg-gradient-to-r from-green-50 to-emerald-100 shadow-xl rounded-xl pointer-events-auto flex ring-1 ring-black ring-opacity-5 relative overflow-hidden`}
        >
          <button
            onClick={() => toast.dismiss(t.id)}
            className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors p-1"
          >
            <IoClose size={18} />
          </button>

          <div className="flex-1 w-0 p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 pt-0.5">
                <Image
                  className="h-12 w-12 rounded-lg object-cover shadow-sm border border-white/50"
                  src={Array.isArray(product.image_url) ? product.image_url[0] : (product.image_url || '/placeholder.png')}
                  alt={product.name}
                  width={48}
                  height={48}
                />
              </div>
              <div className="ml-4 flex-1 pr-6">
                <p className="text-sm font-bold text-emerald-900">
                  Added to Cart!
                </p>
                <p className="mt-1 text-sm text-emerald-700 line-clamp-1">
                  {product.name}
                </p>
                <div className="mt-3">
                  <button
                    onClick={() => {
                      toast.dismiss(t.id);
                      openCartDrawer();
                    }}
                    className="text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 px-4 py-2 rounded-lg shadow-sm transition-colors inline-block"
                  >
                    View Cart
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ), {
        duration: 4000,
        position: 'top-center',
      });
    }, 0);
  }, [openCartDrawer, triggerAnimation]);

  const addFreeGiftToCart = useCallback((productOverride?: Product) => {
    const giftProduct = productOverride ?? freeProduct;
    if (!giftProduct) return;

    const freeProductWithPrice = applyFreeProductPrice(giftProduct);
    const isAlreadyInCart = cartItems.some(item => item.id === freeProductWithPrice.id);
    if (!isAlreadyInCart) {
      addToCart(freeProductWithPrice);
      triggerConfetti();
    }
  }, [freeProduct, cartItems, addToCart]);

  const removeFromCart = useCallback((productId: string) => {
    setCartItems((prev) => prev.filter((item) => item.id !== productId));
  }, []);

  const updateQuantity = useCallback((productId: string, newQuantity: number) => {
    setCartItems((prevCartItems) => {
      const item = prevCartItems.find(i => i.id === productId);
      if (item && isFreeProduct(item)) {
        if (newQuantity > 1) {
          showFreeProductLimitNotification();
        }
        return prevCartItems;
      }

      if (newQuantity <= 0) {
        return prevCartItems.filter((item) => item.id !== productId);
      } else if (newQuantity <= 5) {
        return prevCartItems.map((item) =>
          item.id === productId ? { ...item, quantity: newQuantity } : item
        );
      } else {
        showBulkOrderNotification();
        return prevCartItems;
      }
    });
  }, []);

  const replaceCart = useCallback((items: CartItem[]) => {
    const normalizedItems = items
      .filter((item) => (
        isProductAvailable(item)
        || item.metaData?.source === 'expert_dashboard'
        || /_chat_recommend$/.test(String(item.metaData?.source || ''))
        || item.metaData?.recommendation_id
        || item.metaData?.expert_recommended === true
      ))
      .map((item) => {
        const quantity = Math.max(1, Math.min(5, Number(item.quantity || 1)));
        return {
          ...(applyFreeProductPrice(item) as Product),
          quantity,
        };
      });

    setCartItems(normalizedItems);
    if (normalizedItems.length > 0) {
      triggerAnimation();
    }
  }, [triggerAnimation]);

  const clearCart = useCallback(() => {
    setCartItems([]);
  }, []);

  const getItemQuantity = useCallback((productId: string) => {
    const item = cartItems.find((item) => item.id === productId);
    return item ? item.quantity : 0;
  }, [cartItems]);

  const itemCount = cartItems.reduce((total, item) => total + item.quantity, 0);

  const value = useMemo(
    () => ({
      cartItems,
      addToCart,
      removeFromCart,
      updateQuantity,
      replaceCart,
      clearCart,
      getItemQuantity,
      addFreeGiftToCart,
      itemCount,
      isAnimating,
      isCartDrawerOpen,
      openCartDrawer,
      closeCartDrawer,
      toggleCartDrawer,
    }),
    [
      cartItems,
      addToCart,
      removeFromCart,
      updateQuantity,
      replaceCart,
      clearCart,
      getItemQuantity,
      addFreeGiftToCart,
      itemCount,
      isAnimating,
      isCartDrawerOpen,
      openCartDrawer,
      closeCartDrawer,
      toggleCartDrawer,
    ]
  );

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
